import React, { useEffect, useRef } from 'react';

import { useLanguage } from '../_hooks/useLanguage';

/*
 * Map the app locale (e.g. "pt-BR") to a Cloudflare Turnstile language code.
 *
 * EVERY locale this app ships needs an entry. The `|| locale.toLowerCase()`
 * fallback below looks like it covers the gap, but it does not: it would hand
 * Turnstile `pt-pt`, which is not in its supported list, and the widget falls
 * back to browser-detected language — so the challenge can render in a language
 * the visitor did not pick, on the one screen where confusion costs a login.
 * Turnstile has no European Portuguese; `pt` is the closest it offers.
 */
const TURNSTILE_LANGUAGES = {
    'pt-BR': 'pt-br',
    'pt-PT': 'pt',
    'es-ES': 'es',
    'en-US': 'en',
};

const Turnstile = ({
    siteKey,
    onVerify,
    onError,
    onExpire,
    className = '',
    theme = 'light',
    size = 'normal',
    resetTrigger = null
}) => {
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);
    const locale = useLanguage();
    const language = TURNSTILE_LANGUAGES[locale] || locale.toLowerCase();

    useEffect(() => {
        // Load Cloudflare Turnstile script if not already loaded
        if (!window.turnstile) {
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            script.defer = true;
            script.onload = initializeTurnstile;
            document.head.appendChild(script);
        } else {
            initializeTurnstile();
        }

        return () => {
            if (widgetIdRef.current !== null && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // Reset turnstile when resetTrigger changes
        if (resetTrigger !== null && widgetIdRef.current !== null && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
        }
    }, [resetTrigger]);

    useEffect(() => {
        // Re-render the widget when the locale changes so it follows the selected language
        if (widgetIdRef.current !== null && window.turnstile) {
            initializeTurnstile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const initializeTurnstile = () => {
        if (!turnstileRef.current || !window.turnstile || !siteKey) {
            return;
        }

        // Remove existing widget if present
        if (widgetIdRef.current !== null) {
            window.turnstile.remove(widgetIdRef.current);
        }

        // Render new widget
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: siteKey,
            callback: onVerify,
            'error-callback': onError,
            'expired-callback': onExpire,
            theme: theme,
            size: size,
            language: language
        });
    };

    return (
        <div 
            ref={turnstileRef}
            className={`turnstile-widget ${className}`}
        />
    );
};

export default Turnstile;